import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/Home.css';

function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const heroImage = isMobile 
    ? '/images/hero-background-mobile.png' 
    : '/images/hero-background.png';
  
  const clients = [
    { id: 1, src: "/images/logo-enjoy.png", alt: "Enjoy Casino" },
    { id: 2, src: "/images/logo-gam.png", alt: "Centro Cultural GAM" },
    { id: 3, src: "/images/logo-karun.png", alt: "Karün" },
    { id: 4, src: "/images/logo-santa-fe.png", alt: "Grupo Santa Fe" },
    { id: 5, src: "/images/logo-lo-valledor.png", alt: "Mercado Lo Valledor" },
    { id: 6, src: "/images/logo-rotter-krauss.png", alt: "Rotter & Krauss" },
    { id: 7, src: "/images/logo-chubb.png", alt: "CHUBB" },
    { id: 8, src: "/images/logo-caputal.png", alt: "Caputal" }
  ];

  const services = [
    {
      id: 1,
      backgroundImage: "/images/recursos-humanos1.png",
      icon: "/images/icons/icon-security-guard.png",
      title: "Recursos Humanos",
      description: "Guardias permanentes y esporádicos, autorizados por Carabineros de Chile (OS10), uniformados y equipados con radios portátiles.",
      link: "/servicios#recursos-humanos"
    },
    {
      id: 2,
      backgroundImage: "/images/auditorias.jpg",
      icon: "/images/icons/icon-training.png",
      title: "Auditorías y Control",
      description: "Cuantificación del cumplimiento de sistemas de control y funcionamiento operacional de su empresa.",
      link: "/servicios#capacitacion"
    },
    {
      id: 3,
      backgroundImage: "/images/tecnologia.jpg",
      icon: "/images/icons/icon-technology.png",
      title: "Tecnología y Proyectos",
      description: "Control CCTV con analistas especializados y proyectos de monitoreo remoto para su empresa.",
      link: "/servicios#tecnologia"
    }
  ];

  // Mobile carousel logic (unchanged)
  const itemsPerSlide = isMobile ? 1 : 4;
  const totalSlides = Math.ceil(clients.length / itemsPerSlide);

  useEffect(() => {
    if (!isMobile) return; // Desktop uses CSS animation, no JS needed
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 4000);
    return () => clearInterval(interval);
  }, [totalSlides, isMobile]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Duplicate clients for seamless infinite loop on desktop
  const desktopClients = [...clients, ...clients];

  return (
    <div className="home">
      {/* Hero Section */}
      <section 
        className="hero"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.4) 100%), url(${heroImage})`
        }}
      >
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1>Compromiso con la Seguridad</h1>
          <p>Servicios integrales en recursos humanos, tecnología, asesoría y capacitación con más de 12 años de experiencia</p>
          <div className="hero-buttons">
            <Link to="/servicios" className="btn btn-primary">Nuestros Servicios</Link>
            <Link to="/contacto" className="btn btn-secondary">Solicitar Cotización</Link>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="services-preview">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Lo que hacemos</span>
            <h2>Servicios Integrales de Seguridad</h2>
            <p>Soluciones integrales, eficientes y personalizadas adaptadas a tus necesidades</p>
          </div>

          <div className="services-grid">
            {services.map((service) => (
              <div 
                key={service.id}
                className="service-card"
                style={{ backgroundImage: `url(${service.backgroundImage})` }}
              >
                <div className="service-icon">
                  <img src={service.icon} alt={service.title} />
                </div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <Link to={service.link} className="service-link">
                  Ver más <span>→</span>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="why-us">
        <div className="container">
          <div className="why-us-content">
            <div className="why-us-image">
              <img 
                src="/images/security-professional.jpg" 
                alt="Profesionales de Seguridad"
                loading="lazy"
              />
              <div className="experience-badge">
                <span className="experience-number">12+</span>
                <span className="experience-text">Años de Experiencia</span>
              </div>
            </div>

            <div className="why-us-text">
              <span className="section-tag">Por qué elegirnos</span>
              <h2>Compromiso con tu Seguridad</h2>
              <p className="lead">Marcus Security es una empresa conformada por un grupo de profesionales cuyo principal objetivo es dar soluciones a problemáticas que puedan afectar la operatividad de nuestros clientes. Operamos bajo altos estándares internacionales, basados en Normas ISO, leyes y decretos nacionales.</p>
              
              <div className="features-list">
                <div className="feature-item">
                  <div className="feature-icon">✓</div>
                  <div className="feature-content">
                    <h4>Autorización OS10</h4>
                    <p>Empresa bajo número 0805XXXXXXXX de Carabineros de Chile</p>
                  </div>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">✓</div>
                  <div className="feature-content">
                    <h4>Seguros y Respaldo Legal</h4>
                    <p>Seguro de responsabilidad civil hasta 1.000 UF y afiliación a ACHS</p>
                  </div>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">✓</div>
                  <div className="feature-content">
                    <h4>Expertos Certificados</h4>
                    <p>Equipo con expertos asesores certificados en seguridad privada</p>
                  </div>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">✓</div>
                  <div className="feature-content">
                    <h4>Cobertura Regional</h4>
                    <p>Servicios en Regiones Metropolitana, Valparaíso y Antofagasta</p>
                  </div>
                </div>
              </div>

              <Link to="/empresa" className="btn btn-primary">Conoce Más Sobre Nosotros</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Client Testimonials */}
      <section className="testimonials">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Testimonios</span>
            <h2>Lo Que Dicen Nuestros Clientes</h2>
          </div>

          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-stars">★★★★★</div>
              <p>"Excelente servicio y profesionalismo. El personal está siempre atento y bien preparado para cualquier situación."</p>
              <div className="testimonial-author">
                <img src="/images/client-1.jpg" alt="Cliente" loading="lazy" />
                <div><strong>María G.</strong></div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-stars">★★★★★</div>
              <p>"Trabajamos con Marcus Security hace años y siempre han demostrado compromiso y seriedad en su trabajo."</p>
              <div className="testimonial-author">
                <img src="/images/client-2.png" alt="Cliente" loading="lazy" />
                <div><strong>Carlos R.</strong></div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-stars">★★★★★</div>
              <p>"Servicios de seguridad confiables y profesionales. Marcus Security siempre cumple con los más altos estándares."</p>
              <div className="testimonial-author">
                <img src="/images/client-3.jpg" alt="Cliente" loading="lazy" />
                <div><strong>Patricia S.</strong></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Clients */}
      <section className="clients">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Confían en nosotros</span>
            <h2>Empresas que Protegemos</h2>
          </div>
        </div>

        {/* Desktop: infinite auto-scroll carousel (no container constraint) */}
        {!isMobile ? (
          <div className="clients-infinite-wrapper">
            <div className="clients-infinite-track">
              {desktopClients.map((client, index) => (
                <div key={`${client.id}-${index}`} className="client-logo-infinite">
                  <img src={client.src} alt={client.alt} loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* Mobile: original paginated carousel */
          <div className="container">
            <div className="clients-carousel-container">
              <div className="clients-carousel">
                <div 
                  className="clients-track"
                  style={{
                    transform: `translateX(-${currentSlide * (100 / totalSlides)}%)`
                  }}
                >
                  {clients.map((client) => (
                    <div key={client.id} className={`client-logo ${currentSlide === Math.floor((client.id - 1) / itemsPerSlide) ? 'active' : ''}`}>
                      <img src={client.src} alt={client.alt} loading="lazy" />
                    </div>
                  ))}
                </div>
              </div>

              <div className="carousel-dots">
                {[...Array(totalSlides)].map((_, index) => (
                  <button
                    key={index}
                    className={`carousel-dot ${currentSlide === index ? 'active' : ''}`}
                    onClick={() => goToSlide(index)}
                    aria-label={`Ir al slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>¿Necesitas Proteger tu Empresa o Comunidad?</h2>
            <p>Solicita una cotización sin compromiso y descubre cómo podemos ayudarte</p>
            <Link to="/contacto" className="btn btn-light">Solicitar Cotización Gratuita</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;